import {
  type AccountingChartQuery,
  type AccountingJournalQuery,
  type AccountingLedgerQuery,
  type AccountingPeriodQuery,
  type ChartAccount,
  type ChartOfAccountsResponse,
  type CreateAccountingAccountInput,
  type CreatedAccountingAccount,
  type JournalResponse,
  type LedgerResponse,
  type StatementsResponse,
  type TrialBalanceResponse,
} from "@/features/finance/model/accounting-reports-types"
import {
  http,
} from "@/shared/lib/api/http"
import {
  type FinanceHealthResponse,
} from "@ibada/shared-types/finance"
import {
  ZERO_MONEY,
  normalizeMoney,
} from "./finance-api-shared"


/* --- Accounting reports (ADR-0021 tasks 20–22): read-only, derived from the journal --- */

function accountingScopeParams(query: { workspaceId: string; branchId?: string | null; scope?: string }) {
  return {
    workspaceId: query.workspaceId,
    ...(typeof query.branchId !== "undefined" && query.branchId !== null ? { branchId: query.branchId } : {}),
    ...(query.scope ? { scope: query.scope } : {}),
  }
}


export async function getAccountingChart(query: AccountingChartQuery): Promise<ChartOfAccountsResponse> {
  const response = await http.get<unknown>("/finance/accounting/accounts", {
    params: {
      ...accountingScopeParams(query),
      ...(query.dateTo ? { dateTo: query.dateTo } : {}),
      ...(query.type ? { type: query.type } : {}),
    },
  })
  const record = (response.data ?? {}) as Record<string, unknown>
  return { items: Array.isArray(record.items) ? (record.items as ChartAccount[]) : [] }
}

/** Create a first-class income/expense account in the Chart of Accounts (categories→accounts convergence). */
export async function createAccountingAccount(
  input: CreateAccountingAccountInput,
): Promise<CreatedAccountingAccount> {
  const response = await http.post<unknown>("/finance/accounting/accounts", {
    workspaceId: input.workspaceId,
    kind: input.kind,
    name: input.name,
    // Sent only when the account is the branch's own — omitted entirely for a workspace-wide one.
    ...(input.owningBranchId ? { branchId: input.owningBranchId } : {}),
  })
  const record = (response.data ?? {}) as Record<string, unknown>
  return {
    id: String(record.id ?? ""),
    code: String(record.code ?? ""),
    name: String(record.name ?? ""),
  }
}


export async function getAccountingJournal(query: AccountingJournalQuery): Promise<JournalResponse> {
  const response = await http.get<unknown>("/finance/accounting/journal", {
    params: {
      ...accountingScopeParams(query),
      ...(query.dateFrom ? { dateFrom: query.dateFrom } : {}),
      ...(query.dateTo ? { dateTo: query.dateTo } : {}),
      ...(query.search ? { search: query.search } : {}),
      ...(typeof query.page === "number" ? { page: query.page } : {}),
      ...(typeof query.limit === "number" ? { limit: query.limit } : {}),
    },
  })
  const record = (response.data ?? {}) as Partial<JournalResponse>
  return {
    items: Array.isArray(record.items) ? record.items : [],
    total: typeof record.total === "number" ? record.total : 0,
    page: typeof record.page === "number" ? record.page : query.page ?? 1,
    limit: typeof record.limit === "number" ? record.limit : query.limit ?? 25,
  }
}


export async function getAccountingLedger(query: AccountingLedgerQuery): Promise<LedgerResponse> {
  const response = await http.get<unknown>("/finance/accounting/ledger", {
    params: {
      ...accountingScopeParams(query),
      accountingAccountId: query.accountingAccountId,
      ...(query.dateFrom ? { dateFrom: query.dateFrom } : {}),
      ...(query.dateTo ? { dateTo: query.dateTo } : {}),
      ...(query.fundId ? { fundId: query.fundId } : {}),
    },
  })
  return response.data as LedgerResponse
}


export async function getAccountingTrialBalance(query: AccountingPeriodQuery): Promise<TrialBalanceResponse> {
  const response = await http.get<unknown>("/finance/accounting/trial-balance", {
    params: {
      ...accountingScopeParams(query),
      ...(query.dateFrom ? { dateFrom: query.dateFrom } : {}),
      ...(query.dateTo ? { dateTo: query.dateTo } : {}),
    },
  })
  const record = (response.data ?? {}) as Partial<TrialBalanceResponse>
  return {
    rows: Array.isArray(record.rows) ? record.rows : [],
    // The totals are the SERVER's — and `balanced` is the server's verdict, decided on Decimals. The browser
    // does not re-add the columns to check, which is exactly what it must not do.
    totalDebit: normalizeMoney(record.totalDebit) ?? ZERO_MONEY,
    totalCredit: normalizeMoney(record.totalCredit) ?? ZERO_MONEY,
    balanced: Boolean(record.balanced),
  }
}


export async function getAccountingStatements(query: AccountingPeriodQuery): Promise<StatementsResponse> {
  const response = await http.get<unknown>("/finance/accounting/statements", {
    params: {
      ...accountingScopeParams(query),
      ...(query.dateFrom ? { dateFrom: query.dateFrom } : {}),
      ...(query.dateTo ? { dateTo: query.dateTo } : {}),
    },
  })
  return response.data as StatementsResponse
}


/**
 * Financial health (IBA-117): the ratios AND their verdicts, computed on the server from the ledger. The
 * browser used to divide the statements' totals by each other and decide good/warning/critical itself.
 */
export async function getFinanceHealth(query: AccountingPeriodQuery): Promise<FinanceHealthResponse> {
  const response = await http.get<FinanceHealthResponse>("/finance/overview/health", {
    params: {
      ...accountingScopeParams(query),
      ...(query.dateFrom ? { dateFrom: query.dateFrom } : {}),
      ...(query.dateTo ? { dateTo: query.dateTo } : {}),
    },
  })
  return response.data
}
